/**
 * FinancialMetricsCard - Summary metrics display
 * 
 * Shows key financial metrics: NPV, IRR, payback, LCOE in a card format.
 */

import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Tooltip,
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    Schedule as ScheduleIcon,
    AccountBalance as AccountBalanceIcon,
    Bolt as BoltIcon,
} from '@mui/icons-material';
import { vortxColors } from '@vortx/shared-ui';

/**
 * Format currency
 */
function formatCurrency(value) {
    if (value === null || value === undefined) return '-';
    return value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    });
}

/**
 * Single metric display
 */
function MetricItem({ icon, label, value, subValue, color = 'inherit', tooltip }) {
    const content = (
        <Box sx={{ textAlign: 'center', p: 2 }}>
            <Box sx={{ color: vortxColors.gold, mb: 1 }}>{icon}</Box>
            <Typography variant="caption" color="textSecondary" display="block">
                {label}
            </Typography>
            <Typography variant="h5" fontWeight={600} color={color}>
                {value}
            </Typography>
            {subValue && (
                <Typography variant="caption" color="textSecondary">
                    {subValue}
                </Typography>
            )}
        </Box>
    );

    if (tooltip) {
        return <Tooltip title={tooltip}>{content}</Tooltip>;
    }
    return content;
}

/**
 * FinancialMetricsCard Component
 */
function FinancialMetricsCard({ metrics, systemCosts }) {
    if (!metrics) {
        return (
            <Card variant="outlined">
                <CardContent>
                    <Typography color="textSecondary" textAlign="center">
                        Run optimization to see financial metrics
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const { npv, irr, paybackPeriod, lcoe, roi } = metrics;
    const totalCapex = systemCosts?.totalCost || 0;

    return (
        <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: vortxColors.charcoal, fontWeight: 600 }}>
                    Key Financial Metrics
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                        <MetricItem
                            icon={<AccountBalanceIcon />}
                            label="Net Present Value"
                            value={formatCurrency(npv)}
                            color={npv >= 0 ? 'success.main' : 'error.main'}
                            tooltip="Present value of all future cash flows minus initial investment"
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <MetricItem
                            icon={<TrendingUpIcon />}
                            label="Internal Rate of Return"
                            value={irr ? `${(irr * 100).toFixed(1)}%` : 'N/A'}
                            color={irr > 0.08 ? 'success.main' : 'warning.main'}
                            tooltip="Discount rate at which NPV equals zero"
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <MetricItem
                            icon={<ScheduleIcon />}
                            label="Simple Payback"
                            value={paybackPeriod > 0 ? `${paybackPeriod.toFixed(1)} yrs` : 'Never'}
                            color={paybackPeriod > 0 && paybackPeriod < 10 ? 'success.main' : 'warning.main'}
                            tooltip="Time to recover initial investment from savings"
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <MetricItem
                            icon={<BoltIcon />}
                            label="LCOE"
                            value={lcoe ? `$${(lcoe * 100).toFixed(2)}/kWh` : 'N/A'}
                            subValue="Levelized Cost"
                            tooltip="Levelized cost of energy from the system"
                        />
                    </Grid>
                </Grid>

                {totalCapex > 0 && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">
                            Total Capital Investment: <strong>{formatCurrency(totalCapex)}</strong>
                        </Typography>
                        {roi && (
                            <Typography variant="body2" color="textSecondary">
                                Lifetime ROI: <strong style={{ color: roi > 0 ? '#4caf50' : '#f44336' }}>
                                    {(roi * 100).toFixed(0)}%
                                </strong>
                            </Typography>
                        )}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

export default FinancialMetricsCard;
